import { ReactElement, ReactNode } from 'react';
import { ComplianceRecord } from '../../../types/ComplianceRecord.types';
import { ReportLogo } from '../../../icons/SvgIcons';

interface ComplianceRecordDrawerFormProps {
  record: ComplianceRecord;
  registeredTradeName: string;
  contactAddress: string;
  placementDate: string;
  onChangeRegisteredTradeName: (value: string) => void;
  onChangeContactAddress: (value: string) => void;
  onChangePlacementDate: (value: string) => void;
  /** Conformity-assessment route ('' = unselected). 'external' requires the notified-body certificate. */
  assessmentRoute: string;
  onChangeAssessmentRoute: (value: 'internal' | 'external') => void;
  /** Inputs are read-only when the user can't manage the list or the record is completed. */
  disabled: boolean;
  /** Reuses the parent's check/empty circle so verifications match the rest of the drawer. */
  renderCircle: (confirmed: boolean) => ReactNode;
}

const dash = (value: string | null | undefined): string =>
  value && value.trim() ? value : '—';

const titleCase = (value: string | null | undefined): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : '—';

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

// Same palette the compliance-record.hbs template uses for the classification badge.
const classificationBadge = (
  classification: string | null
): { label: string; className: string } => {
  const key = (classification ?? '').toLowerCase().replace(/_/g, '-');
  const palette: Record<string, string> = {
    'minimal-risk': 'bg-[#D0FBEB] text-[#027A69]',
    'limited-risk': 'bg-[#FEF3C7] text-[#92400E]',
    'high-risk': 'bg-[#FFEDD5] text-[#9A3412]',
    'high-risk-exception': 'bg-[#FFEDD5] text-[#9A3412]',
    prohibited: 'bg-[#FFE4E6] text-[#9F1239]'
  };
  const label = classification
    ? classification
        .toLowerCase()
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join('-')
    : '—';
  return { label, className: palette[key] ?? 'bg-[#EEEEF0] text-[#4B5563]' };
};

const MetaItem = ({
  label,
  value
}: {
  label: string;
  value: string;
}): ReactElement => (
  <div className="flex flex-col gap-1">
    <span className="text-xs font-medium text-gray-550 uppercase tracking-[0.3px]">
      {label}
    </span>
    <span className="text-sm font-medium text-gray-900 break-words">{value}</span>
  </div>
);

const Card = ({ children }: { children: ReactNode }): ReactElement => (
  <div className="rounded-lg border border-gray-200 bg-white p-4">{children}</div>
);

const SectionHeader = ({ children }: { children: ReactNode }): ReactElement => (
  <h4 className="text-xs font-medium text-gray-550 uppercase mb-3">{children}</h4>
);

const inputClasses =
  'mt-1 w-full border border-gray-200 rounded-md px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-500';

/**
 * Renders the compliance record inside the drawer using the same visual form as
 * the generated PDF (header card, verifications, retained documents, importer
 * identity & retention, disclaimer). The importer identity fields are inline
 * inputs; everything else is read-only. The signature block is intentionally
 * omitted here — signing happens through the drawer's "Generate & Sign" button.
 */
export const ComplianceRecordDrawerForm = ({
  record,
  registeredTradeName,
  contactAddress,
  placementDate,
  onChangeRegisteredTradeName,
  onChangeContactAddress,
  onChangePlacementDate,
  assessmentRoute,
  onChangeAssessmentRoute,
  disabled,
  renderCircle
}: ComplianceRecordDrawerFormProps): ReactElement => {
  const { header } = record;
  const badge = classificationBadge(header.classification);

  return (
    <div className="space-y-4">
      {/* Header card */}
      <Card>
        <div className="flex items-start justify-between gap-4">
          <h3 className="text-base font-semibold text-gray-900">{record.title}</h3>
          <div className="shrink-0">
            <ReportLogo />
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-gray-100 pt-3">
          <MetaItem label="Entity type" value={titleCase(header.entityType)} />
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-550 uppercase tracking-[0.3px]">
              Classification
            </span>
            <span
              className={`inline-flex w-fit rounded-full px-3 py-1 text-xs font-medium ${badge.className}`}
            >
              {badge.label}
            </span>
          </div>
          <MetaItem label="AI system" value={dash(header.systemName)} />
          <MetaItem label="Version" value={dash(header.systemVersion)} />
          <MetaItem label="Organization" value={dash(header.organization)} />
          <MetaItem
            label="Compliance contact"
            value={dash(header.complianceContact)}
          />
          <MetaItem label="Record owner" value={dash(header.ownerName)} />
          <MetaItem label="Date generated" value={formatDate(header.generatedAt)} />
          <MetaItem label="Record ID" value={dash(header.recordId)} />
        </div>
      </Card>

      {/* Verifications & attestations */}
      <Card>
        <SectionHeader>Verifications &amp; attestations</SectionHeader>
        <p className="text-sm text-gray-900v2 mb-3">
          Signing this record attests that you guarantee the following
          verifications:
        </p>
        <ul className="space-y-2">
          {record.verifications.map((v) => (
            <li key={v.key} className="flex items-start gap-1 text-gray-550">
              {renderCircle(v.confirmed)}
              <span className="text-sm">{v.label}</span>
            </li>
          ))}
        </ul>
      </Card>

      {/* Retained documents */}
      {record.documents.length > 0 && (
        <Card>
          <SectionHeader>Retained documents</SectionHeader>
          <ul className="space-y-2">
            {record.documents.map((d, i) => (
              <li key={i} className="flex flex-col gap-0.5">
                <span className="text-xs font-medium text-gray-550">{d.label}</span>
                <span className="text-sm text-gray-800 break-words">{d.fileName}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Importer identity & retention — inline inputs */}
      {record.importer && (
        <Card>
          <SectionHeader>Importer identity &amp; retention</SectionHeader>
          <div className="space-y-3">
            <label className="block text-sm">
              <span className="text-gray-700">
                Registered trade name / trade mark
              </span>
              <input
                className={inputClasses}
                value={registeredTradeName}
                onChange={(e) => onChangeRegisteredTradeName(e.target.value)}
                disabled={disabled}
              />
            </label>
            <label className="block text-sm">
              <span className="text-gray-700">Contact address</span>
              <input
                className={inputClasses}
                value={contactAddress}
                onChange={(e) => onChangeContactAddress(e.target.value)}
                disabled={disabled}
              />
            </label>
            <label className="block text-sm">
              <span className="text-gray-700">Placed on the market (date)</span>
              <input
                type="date"
                className={inputClasses}
                value={placementDate}
                onChange={(e) => onChangePlacementDate(e.target.value)}
                disabled={disabled}
              />
            </label>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-gray-550 uppercase tracking-[0.3px]">
                Retain until (10 years)
              </span>
              <span className="text-sm font-medium text-gray-900">
                {dash(record.importer.retentionEndDate)}
              </span>
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-gray-700">
                Was the conformity assessment carried out by a notified body
                (external route)?
              </span>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-gray-800">
                  <input
                    type="radio"
                    name="assessmentRoute"
                    checked={assessmentRoute === 'external'}
                    onChange={() => onChangeAssessmentRoute('external')}
                    disabled={disabled}
                  />
                  Yes (external)
                </label>
                <label className="flex items-center gap-2 text-gray-800">
                  <input
                    type="radio"
                    name="assessmentRoute"
                    checked={assessmentRoute === 'internal'}
                    onChange={() => onChangeAssessmentRoute('internal')}
                    disabled={disabled}
                  />
                  No (internal)
                </label>
              </div>
              <span className="text-xs text-gray-500">
                External (notified-body) route requires the notified-body
                certificate to be uploaded in Step 1 before signing.
              </span>
            </div>
          </div>
        </Card>
      )}

      {/* Disclaimer */}
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-xs leading-relaxed text-gray-600">
        {record.disclaimer}
      </div>
    </div>
  );
};
